import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS, ROUTE_IDS } from '../routes/ids.js';
import { getRouteById } from '../routes/effective-routes.js';
const tk = (value) => value;
const fieldId = (value) => value;
function metadata(id) {
    return {
        id: id,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        privacyClassification: 'CONFIDENTIAL',
        telemetryId: `experience.feeRules.${id}`,
        accessibilityLabelKey: tk('investment.data.feeRules.title'),
        availability: 'AVAILABLE',
        owner: 'fee-rule',
    };
}
function field(name, type, required = false, options) {
    return {
        id: fieldId(`field.feeRule.${name}`),
        name,
        labelKey: tk(`investment.data.feeRules.fields.${name}`),
        fieldType: type,
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        ...(options ? { options } : {}),
        ...(required
            ? {
                validation: {
                    rules: [{ ruleId: 'required', messageKey: tk('validation.required') }],
                    validateWhen: 'submit',
                },
            }
            : {}),
        layout: { md: type === 'textarea' ? 12 : 6 },
    };
}
function withRules(base, rules) {
    return {
        ...base,
        validation: {
            ...base.validation,
            rules: [...(base.validation?.rules ?? []), ...rules],
            validateWhen: base.validation?.validateWhen ?? 'submit',
        },
    };
}
const saveAction = {
    ...metadata('action.feeRule.create'),
    id: 'action.feeRule.create',
    operationId: OPERATION_IDS.FEE_RULE_CREATE_VERSION,
    labelKey: tk('action.save'),
    iconKey: 'save',
    kind: 'command',
    commandId: 'cmd.admin.fee-rules.create-version',
    submitBehavior: 'form_submit',
    submitConfig: {
        validatesForm: true,
        requiresDirty: true,
        requiresValid: true,
        resetBehavior: 'toServerValues',
    },
    intent: 'primary',
    idempotencyRequired: true,
    successBehavior: 'NAVIGATE_BACK',
    successMessageKey: tk('common.saveSuccess'),
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: tk('common.error.unexpected'),
    order: 10,
    group: 'primary',
};
function lifecycleAction(name, operationId, _status, intent) {
    const actionId = `action.feeRule.${name}`;
    return {
        ...metadata(actionId),
        id: actionId,
        operationId,
        labelKey: tk(`action.${name}`),
        iconKey: name,
        kind: 'command',
        commandId: `cmd.admin.fee-rules.${name}`,
        submitBehavior: 'standalone',
        intent,
        idempotencyRequired: true,
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: tk('common.saveSuccess'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        order: name === 'submit' ? 10 : name === 'approve' ? 20 : name === 'suspend' ? 30 : 40,
        group: intent === 'danger' ? 'danger' : 'primary',
    };
}
const jurisdictionField = {
    ...field('jurisdiction', 'hidden'),
    value: {
        defaultValue: 'NP',
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'include',
    },
};
// The backend currently supports exactly one posting fee-rule family. Keep the
// value in the canonical descriptor but do not ask users to choose impossible
// alternatives that the DTO/service will reject.
const ruleTypeField = {
    ...field('ruleType', 'hidden'),
    value: {
        defaultValue: 'BROKERAGE_SEBON_DP',
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'include',
        retainWhenHidden: true,
    },
};
const DEDUCTIBLE_CLASSIFICATIONS = [
    ['DEDUCTIBLE', 'investment.data.feeRules.deductibleOptions.deductible'],
    ['NON_DEDUCTIBLE', 'investment.data.feeRules.deductibleOptions.nonDeductible'],
    ['PARTIAL', 'investment.data.feeRules.deductibleOptions.partial'],
];
const deductibleClassificationField = {
    ...field('deductibleClassification', 'select', true, DEDUCTIBLE_CLASSIFICATIONS.map(([value, labelKey]) => ({ value, labelKey: tk(labelKey) }))),
    helpTextKey: tk('investment.data.feeRules.help.deductibleClassification'),
    value: {
        defaultValue: 'DEDUCTIBLE',
        hiddenValuePolicy: 'unregister',
        submitBehavior: 'include',
    },
};
const ROUNDING_POLICIES = [
    ['ROUND_HALF_UP_2DP', 'investment.data.feeRules.roundingOptions.halfUp2'],
    ['ROUND_HALF_EVEN_2DP', 'investment.data.feeRules.roundingOptions.halfEven2'],
    ['ROUND_DOWN_2DP', 'investment.data.feeRules.roundingOptions.down2'],
    ['ROUND_UP_2DP', 'investment.data.feeRules.roundingOptions.up2'],
    ['ROUND_HALF_UP_0DP', 'investment.data.feeRules.roundingOptions.halfUp0'],
    ['ROUND_HALF_EVEN_0DP', 'investment.data.feeRules.roundingOptions.halfEven0'],
];
const roundingPolicyField = {
    ...field('roundingPolicy', 'select', false, ROUNDING_POLICIES.map(([value, labelKey]) => ({ value, labelKey: tk(labelKey) }))),
    value: {
        defaultValue: 'ROUND_HALF_UP_2DP',
        hiddenValuePolicy: 'unregister',
        submitBehavior: 'include',
    },
    helpTextKey: tk('investment.data.feeRules.help.roundingPolicy'),
};
const componentsField = {
    ...field('components', 'textarea'),
    helpTextKey: tk('investment.data.feeRules.help.components'),
    value: {
        defaultValue: JSON.stringify([
            { component: 'brokerage', kind: 'RATE', base: 'GROSS_VALUE', rate: '0.004', roundingScale: 2, roundingMode: 'ROUND_HALF_UP', roundingStage: 'COMPONENT', appliesTo: 'BOTH', deductible: true },
            { component: 'dp', kind: 'RATE', base: 'GROSS_VALUE', rate: '0.00015', roundingScale: 2, roundingMode: 'ROUND_HALF_UP', roundingStage: 'COMPONENT', appliesTo: 'BOTH', deductible: true },
            { component: 'sebon', kind: 'RATE', base: 'GROSS_VALUE', rate: '0.00015', roundingScale: 2, roundingMode: 'ROUND_HALF_UP', roundingStage: 'COMPONENT', appliesTo: 'BOTH', deductible: true },
        ], null, 2),
        hiddenValuePolicy: 'preserve',
        submitBehavior: 'include',
    },
    layout: { md: 12 },
};
function feeRuleFormDescriptor(mode) {
    const isView = mode === 'view';
    return {
        ...metadata(`form.admin.feeRule.${mode}`),
        id: `form.admin.feeRule.${mode}`,
        titleKey: isView ? tk('investment.data.feeRules.title') : tk('investment.data.feeRules.actions.create'),
        descriptionKey: tk('investment.data.feeRules.description'),
        mode,
        fields: [
            jurisdictionField,
            ruleTypeField,
            deductibleClassificationField,
            roundingPolicyField,
            field('effectiveFrom', 'date', true),
            field('effectiveTo', 'date'),
            componentsField,
            field('sourceReference', 'textarea'),
        ],
        sections: [
            {
                id: 'section.feeRule.scope',
                titleKey: tk('investment.data.feeRules.section.scope'),
                fields: ['field.feeRule.jurisdiction', 'field.feeRule.ruleType'].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.feeRule.classification',
                titleKey: tk('investment.data.feeRules.section.classification'),
                fields: ['field.feeRule.deductibleClassification', 'field.feeRule.roundingPolicy'].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.feeRule.effectiveDates',
                titleKey: tk('investment.data.feeRules.section.effectiveDates'),
                fields: ['field.feeRule.effectiveFrom', 'field.feeRule.effectiveTo'].map(fieldId),
                layout: { columns: 2, gap: 16 },
            },
            {
                id: 'section.feeRule.components',
                titleKey: tk('investment.data.feeRules.section.components'),
                fields: ['field.feeRule.components'].map(fieldId),
                layout: { columns: 1, gap: 16 },
            },
            {
                id: 'section.feeRule.reference',
                titleKey: tk('investment.data.feeRules.section.reference'),
                fields: ['field.feeRule.sourceReference'].map(fieldId),
                layout: { columns: 1, gap: 16 },
            },
        ],
        ...(isView
            ? {
                actions: [],
            }
            : {
                actions: [saveAction],
                validation: { validateWhen: 'submit' },
            }),
        submission: {
            defaultCommandId: 'cmd.admin.fee-rules.create-version',
            successMessageKey: tk('common.saveSuccess'),
            errorMessageKey: tk('common.error.unexpected'),
        },
    };
}
export const FEE_RULE_CREATE_FORM = feeRuleFormDescriptor('create');
export const FEE_RULE_EDIT_FORM = feeRuleFormDescriptor('edit');
export const FEE_RULE_VIEW_FORM = feeRuleFormDescriptor('view');
function column(name, overrides = {}) {
    return {
        ...metadata(`column.feeRule.${name}`),
        labelKey: tk(`investment.data.feeRules.fields.${name}`),
        field: name,
        fieldType: 'text',
        sortable: false,
        filterable: false,
        searchable: false,
        groupable: false,
        hideable: true,
        defaultVisible: true,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: 150,
        minWidth: 90,
        maxWidth: 320,
        align: 'left',
        nullDisplayBehavior: 'dash',
        ...overrides,
    };
}
export const FEE_RULE_TABLE = {
    ...metadata('table.admin.feeRules'),
    id: 'table.admin.feeRules',
    titleKey: tk('investment.data.feeRules.title'),
    dataSourceId: 'query.admin.fee-rules.list',
    rowIdField: 'id',
    columns: [
        column('jurisdiction', {
            requiredVisible: true,
            sortable: true,
            filterable: true,
            searchable: true,
            allowedFilterOperators: ['equals'],
        }),
        column('ruleType', { defaultVisible: false }),
        column('deductibleClassification'),
        column('componentSummary', { requiredVisible: true, searchable: true }),
        column('effectiveFrom', { fieldType: 'date', sortable: true }),
        column('effectiveTo', { fieldType: 'date' }),
        column('status', {
            fieldType: 'status',
            requiredVisible: true,
            sortable: true,
            filterable: true,
            allowedFilterOperators: ['equals'],
        }),
        column('version', { fieldType: 'number', sortable: true }),
        column('roundingPolicy', { defaultVisible: false }),
        column('sourceReference', { defaultVisible: false }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    defaultSort: { field: 'effectiveFrom', direction: 'desc' },
    rowActions: [
        lifecycleAction('submit', OPERATION_IDS.FEE_RULE_SUBMIT, 'DRAFT', 'primary'),
        lifecycleAction('approve', OPERATION_IDS.FEE_RULE_APPROVE, 'PENDING_APPROVAL', 'primary'),
        lifecycleAction('suspend', OPERATION_IDS.FEE_RULE_SUSPEND, 'ACTIVE', 'warning'),
        lifecycleAction('retire', OPERATION_IDS.FEE_RULE_RETIRE, 'SUSPENDED', 'danger'),
    ],
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: true,
    serverSideFiltering: true,
    pageActions: [
        {
            ...metadata('action.feeRules.import'),
            id: 'action.feeRules.import',
            operationId: OPERATION_IDS.IMPORT_UPLOAD,
            labelKey: tk('action.import'),
            iconKey: 'upload',
            kind: 'navigate',
            intent: 'secondary',
            idempotencyRequired: false,
            successBehavior: 'NAVIGATE_TO',
            targetRouteId: ROUTE_IDS.DATA_IMPORT_UPLOAD,
            successNavigateTo: `${getRouteById(ROUTE_IDS.DATA_IMPORT_UPLOAD)?.path ?? '/data/imports/upload'}?importType=${encodeURIComponent('FEE_RULE')}`,
            errorBehavior: 'SHOW_INLINE_ERROR',
            order: 20,
            group: 'secondary',
        },
    ],
    capabilities: {
        sorting: true,
        filtering: true,
        globalSearch: true,
        pagination: true,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        export: true,
        refresh: true,
        density: true,
        create: true,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
};
